<template>
  <div class="scrollable-div display-scrollbar">
    <table class="full-width-table quote-products-table">
      <tbody>
        <tr class="table-header">
          <th style="width: 10%">Part #</th>
          <th style="width: 10%">Old Part #</th>
          <th style="width: 14%">Description</th>
          <th style="width: 12%">Color</th>
          <th style="width: 5%">UOM</th>
          <th style="width: 7%">Weight</th>
          <th style="width: 8%">Pc/FT2</th>
          <th v-for="priceList in priceLists" :key="priceList.PICKLISTITEMID">{{ priceList.NAME }}</th>
          <th style="width: 3rem"></th>
        </tr>
        <tr v-if="loadingQuoteProducts" class="loading-row">
          <td :colspan="8 + priceLists.length"><img src="../../assets/spinner.svg" /></td>
        </tr>
        <tr v-else-if="!quoteProducts.length">
          <td :colspan="8 + priceLists.length">No quote products found.</td>
        </tr>
        <tr v-else-if="!filteredQuoteProducts.length">
          <td :colspan="8 + priceLists.length">No matching products found.</td>
        </tr>
        <tr v-for="product in filteredQuoteProducts" v-else :key="product.PRODUCTID" class="product-row">
          <td :class="{ link: canManageProducts }" @click="canManageProducts && editProduct(product)">{{ product.PARTNUM }}</td>
          <td>{{ product.OLDPARTNUM }}</td>
          <td>{{ product.DESCRIPTION }}</td>
          <td>{{ product.COLOR }}</td>
          <td>{{ productUom(product) }}</td>
          <td>{{ formatNumber(product.WEIGHT) }}</td>
          <td>{{ formatNumber(product.PIECESPERSQFT) }}</td>
          <td v-for="priceList in priceLists" :key="`${product.PRODUCTID}-${priceList.PICKLISTITEMID}`">
            <span v-if="priceFor(product, priceList.PICKLISTITEMID)">
              {{ formatCurrency(priceFor(product, priceList.PICKLISTITEMID).PRICEPC) }} / {{ productUom(product) }}
              <span v-if="productUom(product) !== 'FT2' && priceFor(product, priceList.PICKLISTITEMID).PRICESQFT !== null && priceFor(product, priceList.PICKLISTITEMID).PRICESQFT !== undefined">
                <br />{{ formatCurrency(priceFor(product, priceList.PICKLISTITEMID).PRICESQFT) }} / FT2
              </span>
            </span>
            <span v-else>—</span>
          </td>
          <td class="context-container">
            <div v-if="canManageProducts" class="hover-icon" @click="editProduct(product)">
              <img id="noHover" src="../../assets/pencil.svg" />
              <img id="hover" src="../../assets/pencil-hover.svg" />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <EditQuoteProduct v-if="showEditProduct" :product="showEditProduct" :priceLists="priceLists" @close="showEditProduct = null" @saved="handleProductsChanged" />
  <QuoteProductImportPopup v-if="showImportPopup" :priceLists="priceLists" @close="showImportPopup = false" @imported="handleProductsChanged" />
</template>

<script>
import useQuoteProductsQuery from '@/composables/useQuoteProductsQuery';
import usePicklistItemsQuery from '@/composables/usePicklistItemsQuery';
import useCurrentUser from '@/composables/useCurrentUser';
import { searchQuoteProducts } from '@/composables/useQuoteProductSearch';
import EditQuoteProduct from '@/components/quotes/EditQuoteProduct.vue';
import QuoteProductImportPopup from '@/components/quotes/QuoteProductImportPopup.vue';

export default {
  name: 'QuoteProducts',
  components: { EditQuoteProduct, QuoteProductImportPopup },
  props: {
    searchText: {
      type: String,
      default: '',
    },
  },
  setup() {
    useQuoteProductsQuery.useQuoteProductsQuery();
    const { user } = useCurrentUser();

    return {
      quoteProducts: useQuoteProductsQuery.quoteProducts,
      loadingQuoteProducts: useQuoteProductsQuery.loadingQuoteProducts,
      refetchQuoteProducts: useQuoteProductsQuery.refetchQuoteProducts,
      pickLists: usePicklistItemsQuery.pickLists,
      user,
    };
  },
  data() {
    return {
      showEditProduct: null,
      showImportPopup: false,
    };
  },
  computed: {
    priceLists() {
      return this.pickLists.quotePricingStructures?.items || [];
    },
    canManageProducts() {
      return this.user.admin || this.user.quotesAdmin;
    },
    filteredQuoteProducts() {
      if (!this.searchText.trim()) return this.quoteProducts;
      return searchQuoteProducts(this.quoteProducts, this.searchText, this.quoteProducts.length);
    },
  },
  methods: {
    add() {
      if (!this.canManageProducts) return;
      this.showEditProduct = {};
    },
    importProducts() {
      if (!this.canManageProducts) return;
      this.showImportPopup = true;
    },
    editProduct(product) {
      if (!this.canManageProducts) return;
      this.showEditProduct = product;
    },
    handleProductsChanged() {
      this.showEditProduct = null;
      this.showImportPopup = false;
      if (this.refetchQuoteProducts) this.refetchQuoteProducts();
    },
    priceFor(product, priceListId) {
      return product.prices?.find((price) => price.PRICELISTID === priceListId);
    },
    productUom(product) {
      return product?.UOM || 'EA';
    },
    formatCurrency(value) {
      if (value === null || value === undefined || value === '') return '—';
      return `$${Number(value).toFixed(2)}`;
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '';
      return Number(value).toLocaleString(undefined, { maximumFractionDigits: 4 });
    },
  },
};
</script>

<style lang="scss" scoped>
.quote-products-table {
  min-width: 1400px;

  .product-row {
    vertical-align: top;
  }
}
</style>
